import { Component } from '@theme/component';

/**
 * Featured Blog Posts section.
 *
 * Canonical Swiper pattern (matches ax-slider-stories, ax-tabbed-product-lists,
 * ax-usage-tips): `<swiper-container init="false">` with `<swiper-slide>` rendered
 * directly in Liquid. This component calls `.initialize()` once at connect with
 * a breakpoints config.
 *
 * @extends {Component}
 */
class FeaturedBlogPosts extends Component {
  /** @type {any} */
  #swiper = null;

  async connectedCallback() {
    super.connectedCallback();
    await customElements.whenDefined('swiper-container');
    this.#initSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    try {
      this.#swiper?.destroy(true, true);
    } catch (_) {
      // Swiper can throw during teardown if already destroyed.
    }
    this.#swiper = null;
  }

  previous() {
    this.#swiper?.slidePrev();
  }

  next() {
    this.#swiper?.slideNext();
  }

  /** Grise la flèche en butée (début/fin du carousel). */
  #syncArrows(swiper) {
    const prevBtn = /** @type {HTMLButtonElement|null} */ (this.querySelector('.slideshow-control--previous'));
    const nextBtn = /** @type {HTMLButtonElement|null} */ (this.querySelector('.slideshow-control--next'));
    if (!prevBtn && !nextBtn) return;

    const sync = () => {
      if (prevBtn) prevBtn.disabled = Boolean(swiper.isBeginning);
      if (nextBtn) nextBtn.disabled = Boolean(swiper.isEnd);
    };
    sync();
    swiper.on('slideChange', sync);
    swiper.on('reachBeginning', sync);
    swiper.on('reachEnd', sync);
    swiper.on('resize', sync);
  }

  #initSwiper() {
    const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
      this.querySelector('swiper-container.featured-blog-posts__swiper')
    );
    if (!swiperEl || swiperEl.swiper) return;

    const gapDesktop = parseInt(swiperEl.dataset.gapDesktop ?? '16', 10) || 0;
    const gapMobile = parseInt(swiperEl.dataset.gapMobile ?? '16', 10) || 0;
    const columns = parseInt(swiperEl.dataset.columns ?? '3', 10) || 3;
    const mobileSlidesPerView = parseFloat(swiperEl.dataset.mobileSlidesPerView ?? '1.2') || 1.2;

    Object.assign(swiperEl, {
      slidesPerView: mobileSlidesPerView,
      spaceBetween: gapMobile,
      grabCursor: true,
      a11y: true,
      keyboard: { enabled: true },
      watchOverflow: true,
      centerInsufficientSlides: true,
      breakpoints: {
        750: {
          slidesPerView: columns,
          spaceBetween: gapDesktop,
        },
      },
    });

    swiperEl.initialize();
    swiperEl.removeAttribute('init');
    this.#swiper = swiperEl.swiper;
    this.#syncArrows(swiperEl.swiper);

    // Force-load all CFH-lazy slides — the IntersectionObserver can't reach
    // off-viewport slides inside an overflow:hidden carousel.
    document.dispatchEvent(
      new CustomEvent('removeLazyKeep', {
        detail: { lazyParentElement: swiperEl },
      }),
    );
  }
}

if (!customElements.get('featured-blog-posts')) {
  customElements.define('featured-blog-posts', FeaturedBlogPosts);
}
